import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { HSGAppointmentType } from '@shared/services/healthiersg/appointments/types/getHSGAppointment.types';
import type { IClinic } from '@shared/services/healthiersg/enrollment/types/getRecommendedPcp.types';
import type { IEnrollmentInfo } from '@shared/services/healthiersg/enrollment/types/saveEnrolment.types';
import type {
  IActivity,
  IExerciseItem,
} from '@shared/services/healthiersg/healthplan/types/getExercisePlanDetails.types';
import type { RecommendedScreening } from '@shared/services/healthiersg/records/types/getScreeningRecommended';
import type { TemplateFormItem } from '@shared/types/form.types';

import { initialState } from './initialState';
import type { FilteredClinics, ScreeningInfo } from '../types/healthierSG.types';

const healthierSGSlice = createSlice({
  initialState,
  name: 'HealthierSG',
  reducers: {
    resetFilteredClinicsSearch: (state) => {
      state.filteredClinicsSearch = initialState.filteredClinicsSearch;
    },
    resetHSGAppointment: (state) => {
      state.hsgBookAppointment = null;
    },
    setEnrollmentInfo: (state, action: PayloadAction<IEnrollmentInfo>) => {
      state.enrollmentInfo = action.payload;
    },
    // Filtered Clinics
    setFilteredClinicsSearch: (state, action: PayloadAction<FilteredClinics>) => {
      state.filteredClinicsSearch = action.payload;
    },
    setHSGAppointment: (state, action: PayloadAction<HSGAppointmentType>) => {
      state.hsgBookAppointment = action.payload;
    },
    setIsFromGetVaccinatedPage: (state, action: PayloadAction<boolean>) => {
      state.isFromGetVaccinatedPage = action.payload;
    },
    setIsFromHSGRequestFormPage: (state, action: PayloadAction<boolean>) => {
      state.isFromHSGRequestFormPage = action.payload;
    },
    setKakiBadge: (state, action: PayloadAction<boolean>) => {
      state.kakiBadge = action.payload;
    },
    setIsVaccination: (state, action: PayloadAction<boolean>) => {
      if (state.hsgBookAppointment) {
        state.hsgBookAppointment.isVaccination = action.payload;
      }
    },
    setLifestyleArticleCode: (state, action: PayloadAction<string>) => {
      state.lifestyle.articleCode = action.payload;
    },
    setScreeningInfo: (state, action: PayloadAction<ScreeningInfo>) => {
      state.screeningInfo = action.payload;
    },
    setScreeningInfoForBookAppt: (state, action: PayloadAction<RecommendedScreening[]>) => {
      state.screeningInfoForBookAppt = action.payload;
    },
    // Selected Clinic
    setSelectedClinic: (state, action: PayloadAction<IClinic>) => {
      state.selectedClinic = action.payload;
    },
    setSelectedHealthPlanHistory: (state, action: PayloadAction<{ id: null | string; versionId: null | string }>) => {
      state.healthPlanHistory.selected = action.payload;
    },
    // Smoking Questionnaire
    setSelectedSmokingQuestionnareAnswer: (state, action: PayloadAction<TemplateFormItem>) => {
      state.selectedSmokingAnswer = action.payload;
    },
    setSelectedExercise: (state, action: PayloadAction<IExerciseItem>) => {
      state.selectedExercise = action.payload;
    },
    setAdditionalExerciseNotes: (state, action: PayloadAction<string[]>) => {
      state.additionalExerciseNotes = action.payload;
    },
    setSelectedActivity: (state, action: PayloadAction<IActivity>) => {
      state.selectedActivity = action.payload;
    },
    setSelectedExerciseDay: (state, action: PayloadAction<null | string>) => {
      state.selectedExerciseDay = action.payload;
    },
  },
});

export const { actions: healthierSGAction } = healthierSGSlice;

export default healthierSGSlice.reducer;
